/** The small pieces every authoring view shows about a job: where it got to, and its budget. */
import { Icon } from '@/components/Icon'
import type { Job, NodeWord, Reference } from './types'

export function hfTree(ref: Reference) {
  return `https://huggingface.co/datasets/${ref.repository}/tree/${ref.commit}/programs/${ref.program}/tasks/${ref.task_id}`
}

export function RefLink({ value, label }: { value?: Reference; label: string }) {
  if (!value) return null
  return <a className="rail-link" href={hfTree(value)} target="_blank" rel="noreferrer">{label} · <code>{value.commit.slice(0, 8)}</code><Icon name="arrow" size={15} /></a>
}

/** Agent job status as a word a developer can act on, plus the tone to show it in. */
export const JOB_STATE: Record<string, { label: string; tone: string }> = {
  queued: { label: 'Waiting for a supervisor', tone: 'wait' },
  capacity_wait: { label: 'Waiting for Codex capacity', tone: 'wait' },
  running: { label: 'Building', tone: 'live' },
  succeeded: { label: 'Authoring complete', tone: 'ok' },
  blocked_expert: { label: 'Returned to the expert', tone: 'warn' },
  blocked_dev: { label: 'Needs a developer', tone: 'err' },
  failed: { label: 'Failed', tone: 'err' },
  cancelled: { label: 'Stopped', tone: 'wait' },
  // Website-side states: a request is waiting in the outbox for the authoring service.
  pending_delivery: { label: 'Submission pending', tone: 'wait' },
  cancellation_requested: { label: 'Stopping', tone: 'wait' },
  unavailable: { label: 'Status unavailable', tone: 'warn' },
  rejected: { label: 'Refused by the authoring service', tone: 'err' },
}

export function jobState(status: string, job?: Pick<Job, 'phase' | 'capacity_wait' | 'job_type'>) {
  if (status === 'running' && job?.phase === 'account_admission') {
    return { label: 'Waiting for subscription admission', tone: 'wait' }
  }
  if (status === 'capacity_wait') {
    const reason = job?.capacity_wait?.reason
    if (reason === 'unsupported_release') return { label: 'Waiting for release approval', tone: 'warn' }
    if (reason === 'service_unavailable') return { label: 'Subscription service unavailable', tone: 'wait' }
  }
  if (job?.job_type === 'workflow-research') {
    if (status === 'running') return { label: 'Researching', tone: 'research' }
    if (status === 'succeeded') return { label: 'Research complete', tone: 'ok' }
  }
  return JOB_STATE[status] ?? { label: status.replaceAll('_', ' '), tone: 'wait' }
}

export function isLive(status: string) {
  return status === 'running' || status === 'queued' || status === 'capacity_wait'
}

/** A website request the authoring service has not answered yet: in progress, not failed. */
export function isPending(status: string) {
  return status === 'pending_delivery' || status === 'cancellation_requested'
}

/**
 * A job is only ever in one of three states worth a colour, which is why the precise
 * word lives in the card's eyebrow and the dot says only this much. The run board's
 * fourth state, `warning`, has no counterpart here: an authoring job either carries
 * on, finishes, or stops and says why.
 */
export type AgentDot = 'running' | 'done' | 'failed'
export function agentDot(status: string): AgentDot {
  if (isLive(status) || isPending(status)) return 'running'
  return status === 'succeeded' ? 'done' : 'failed'
}

export function Budget({ job }: { job: Job }) {
  const attempts = Object.values(job.attempts ?? {})
  const nonInfra = attempts.filter(a => a.classification === 'non_infra').length
  const pending = attempts.filter(a => a.classification === 'pending').length
  return <dl className="kv agent-budget">
    <div><dt>Rollout attempts</dt><dd>{attempts.length} of {job.limits.total}</dd></div>
    <div><dt>Counted against the task</dt><dd>{nonInfra} of {job.limits.non_infra}</dd></div>
    <div><dt>In flight</dt><dd>{pending || '—'}</dd></div>
  </dl>
}

/** The node word to the runs page's dot colours, so the two boards agree on what a colour means. */
const NODE_DOT: Record<string, string> = {
  booting: 'booting', ready: 'idle', idle: 'idle', 'in use': 'running',
  unreachable: 'unreachable', gone: 'stopped', none: 'pending', unknown: 'pending',
}

/** The job's rollout node, said the way the runs page says a node: one word and a dot. */
export function NodeChip({ node, compact = false }: { node?: NodeWord; compact?: boolean }) {
  if (!node) return null
  const title = [node.activity, node.instance_id].filter(Boolean).join(' · ')
  return <span className="agent-node" title={title || undefined}>
    <span className={`node-state-dot ${NODE_DOT[node.state] ?? 'pending'}`} aria-hidden="true" />
    {compact ? node.state : <>node {node.state}</>}
  </span>
}
